<!-- Page header sits on the background, mirroring the dominant
     `.header` + `.subtitle` convention used by faqs / tutors /
     courses / integrations. Don't move it back inside
     `nb-card-header` — see design.md §"Hint Text on Page
     Background" for the global styling that picks this up. -->
<div class="header">
  <div>
    <h2>{{ 'pages.tutors.placements.title' | translate }}</h2>
    <p class="subtitle">{{ 'pages.tutors.placements.hint' | translate }}</p>
  </div>
</div>

<nb-card class="placements-page" alAnchor="placements.table">
  <nb-card-body class="placements-page__body">
    <div *ngIf="!tabsReady" class="placements-lms__loading">
      <nb-spinner status="basic"></nb-spinner>
    </div>

    <nb-tabset *ngIf="tabsReady" (changeTab)="onTabChange($event)">
      <!-- ─────────────── Tab 1: Moodle Plugin ─────────────── -->
      <!-- Die drei Reiter stehen immer. Vorher hingen sie an
           `connectedLms.*`: wer noch nichts verbunden hatte, sah
           ausschliesslich "LTI 1.3" — der einzige Einstieg fuehrte damit
           ueber den Weg, den wir zuletzt empfehlen. -->
      <nb-tab
        tabId="lms"
        [tabTitle]="'pages.tutors.placements.lms.tabTitle' | translate"
        [tabIcon]="{ icon: 'moodle', pack: 'al' }"
        [active]="initialTab === 'lms'"
      >
        <div class="placements-lms">
          <div *ngIf="lmsSitesLoading" class="placements-lms__loading">
            <nb-spinner status="basic"></nb-spinner>
          </div>

          <div
            *ngIf="!lmsSitesLoading && lmsSites.length === 0"
            class="placements-lms__empty"
          >
            <nb-icon icon="link-2-outline"></nb-icon>
            <h5>{{ 'pages.tutors.placements.lms.noSiteTitle' | translate }}</h5>
            <p>{{ 'pages.tutors.placements.lms.noSiteHint' | translate }}</p>
            <a
              routerLink="/integrations"
              [queryParams]="{ open: 'moodle' }"
              alAnchor="placements.connect-site"
              nbButton
              status="primary"
              size="small"
            >
              {{ 'pages.tutors.placements.lms.connectSite' | translate }}
            </a>
            <p class="placements-lms__plugin-link">
              <a [href]="moodlePluginUrl" target="_blank" rel="noopener">
                <nb-icon icon="download-outline"></nb-icon>
                {{ 'pages.tutors.placements.lms.pluginDownload' | translate }}
              </a>
              <a [href]="moodlePluginGuideUrl" target="_blank" rel="noopener">
                {{ 'pages.tutors.placements.lms.pluginGuide' | translate }}
              </a>
            </p>
          </div>

          <div
            *ngIf="!lmsSitesLoading && canSelectMoodleSite && lmsSites.length > 1"
            class="site-card__filter"
          >
            <nb-form-field>
              <nb-icon nbPrefix icon="layers-outline"></nb-icon>
              <nb-select
                fullWidth
                size="small"
                [selected]="selectedMoodleRegistrationId"
                (selectedChange)="onMoodleSiteChange($event)"
                [placeholder]="'pages.tutors.placements.lms.siteFilterPlaceholder' | translate"
              >
                <nb-option *ngFor="let site of lmsSites; trackBy: trackSiteById" [value]="site.id">
                  {{ moodleSiteTitle(site) }}
                  <span *ngIf="site.is_primary">
                    · {{ 'pages.tutors.placements.lms.primarySite' | translate }}
                  </span>
                </nb-option>
              </nb-select>
            </nb-form-field>
          </div>

          <app-placements-site-card
            *ngFor="let site of visibleLmsSites; trackBy: trackSiteById"
            [site]="site"
            [dataSources]="agentDataSources"
            [dozentBots]="agentDozentBots"
            (siteSuperseded)="onSiteSuperseded()"
          ></app-placements-site-card>
        </div>
      </nb-tab>

      <!-- ─────────────── ILIAS ─────────────────────────────── -->
      <!-- Eigene Lasche statt einer Zeile unter „LTI 1.3": ILIAS bringt über
           SOAP eine Kursliste mit, die kein anderes LTI-LMS liefert. -->
      <nb-tab
        tabId="ilias"
        [tabTitle]="'pages.tutors.placements.ilias.tabTitle' | translate"
        [tabIcon]="{ icon: 'ilias', pack: 'al' }"
        [active]="initialTab === 'ilias'"
      >
        <div class="placements-lms">
          <div
            *ngIf="!ltiLoading && iliasRegistrations.length === 0"
            class="placements-lms__empty"
          >
            <nb-icon icon="link-2-outline"></nb-icon>
            <h5>{{ 'pages.tutors.placements.ilias.noSiteTitle' | translate }}</h5>
            <p>{{ 'pages.tutors.placements.ilias.noSiteHint' | translate }}</p>
            <a
              routerLink="/integrations"
              [queryParams]="{ open: 'ilias' }"
              nbButton
              status="primary"
              size="small"
            >
              {{ 'pages.tutors.placements.ilias.connectSite' | translate }}
            </a>
            <p class="placements-lms__plugin-link">
              <a [href]="iliasPluginUrl" target="_blank" rel="noopener">
                <nb-icon icon="download-outline"></nb-icon>
                {{ 'pages.tutors.placements.ilias.pluginDownload' | translate }}
              </a>
              <a [href]="iliasPluginGuideUrl" target="_blank" rel="noopener">
                {{ 'pages.tutors.placements.ilias.pluginGuide' | translate }}
              </a>
            </p>
          </div>

          <app-ilias-placements-card
            *ngFor="let reg of iliasRegistrations; trackBy: trackSiteById"
            [registration]="reg"
            [tutors]="agentTutors"
            [courses]="ltiCourses"
            [dataSources]="agentDataSources"
            [dozentBots]="agentDozentBots"
            [clientId]="clientId"
          ></app-ilias-placements-card>
        </div>
      </nb-tab>

      <!-- ─────────────── Tab 3: LTI 1.3 (übrige) ───────────── -->
      <nb-tab
        tabId="lti"
        [tabTitle]="'pages.tutors.placements.lti.tabTitle' | translate"
        tabIcon="link-2-outline"
        [active]="initialTab === 'lti'"
      >
        <div class="placements-lms">
          <div *ngIf="ltiLoading" class="placements-lms__loading">
            <nb-spinner status="basic"></nb-spinner>
          </div>

          <div
            *ngIf="!ltiLoading && otherLtiRegistrations.length === 0"
            class="placements-lms__empty"
          >
            <nb-icon icon="link-2-outline"></nb-icon>
            <h5>{{ 'pages.tutors.placements.lti.noRegistrationsTitle' | translate }}</h5>
            <p>{{ 'pages.tutors.placements.lti.noRegistrationsHint' | translate }}</p>

            <!-- Dieselben drei Einstiegskacheln wie im Verbindungsdialog, damit
                 die Einrichtung auch von hier startet statt nur zu verlinken. -->
            <div class="lms-choices">
              <button
                *ngFor="let choice of ltiChoices"
                type="button"
                class="lms-choice"
                (click)="openLtiConnect()"
              >
                <img [src]="choice.image" alt="" class="lms-choice__logo" />
                <span class="lms-choice__body">
                  <span class="lms-choice__name">
                    {{ 'dialogs.ltiConnect.choices.' + choice.key + '.name' | translate }}
                    <span *ngIf="choice.beta" class="state-chip is-warning">
                      {{ 'dialogs.ltiConnect.beta' | translate }}
                    </span>
                  </span>
                  <span class="lms-choice__desc">
                    {{ 'dialogs.ltiConnect.choices.' + choice.key + '.desc' | translate }}
                  </span>
                </span>
              </button>
            </div>
          </div>

          <app-lti-placements-card
            *ngFor="let reg of otherLtiRegistrations; trackBy: trackSiteById"
            [registration]="reg"
            [tutors]="agentTutors"
            [courses]="ltiCourses"
            [clientId]="clientId"
          ></app-lti-placements-card>
        </div>
      </nb-tab>

      <!-- ────── Tab 2: Agents — quick matrix ─────────── -->
      <nb-tab
        tabId="agents"
        [tabTitle]="'pages.tutors.placements.quick.tabTitle' | translate"
        [tabIcon]="{ icon: 'orbi', pack: 'al' }"
      >
        <section class="tile-flat site-card">
          <header class="tile-flat__header">
            <div class="site-card__head">
              <div class="site-card__head-left">
                <div class="site-card__title">
                  <h5 class="site-card__name">
                    {{ 'pages.tutors.placements.quick.cardTitle' | translate }}
                  </h5>
                  <span class="site-card__url">
                    {{ 'pages.tutors.placements.quick.hint' | translate }}
                  </span>
                </div>
              </div>
              <div class="site-card__head-right">
                <span class="site-card__count">
                  {{ filteredAgentTutors.length }}
                  {{ 'pages.tutors.placements.quick.agentsCount' | translate }}
                </span>
              </div>
            </div>
          </header>

          <!-- Filter -->
          <div class="site-card__filter">
            <nb-form-field>
              <nb-icon nbPrefix icon="search-outline"></nb-icon>
              <input
                type="text"
                nbInput
                fieldSize="small"
                [(ngModel)]="agentSearch"
                (ngModelChange)="onAgentSearchChange()"
                [placeholder]="'pages.tutors.placements.quick.searchPlaceholder' | translate"
              />
            </nb-form-field>
            <app-pagination
              *ngIf="filteredAgentTutors.length > 0"
              class="site-card__pagination toolbar-pagination"
              [totalItems]="filteredAgentTutors.length"
              [itemsPerPage]="agentPageSize"
              [currentPage]="agentPage"
              [pageSizeOptions]="pageSizeOptions"
              (pageChanged)="onAgentPageChange($event)"
              (itemsPerPageChange)="onAgentPageSizeChange($event)"
            ></app-pagination>
          </div>

          <!-- Empty state -->
          <div *ngIf="filteredAgentTutors.length === 0" class="site-card__empty">
            <p>{{ 'pages.tutors.placements.quick.empty' | translate }}</p>
          </div>

          <!-- Quick-config matrix: Agent · KB · Dozent · Action -->
          <table *ngIf="filteredAgentTutors.length > 0" class="site-card__table">
            <colgroup>
              <col class="col-agent" />
              <col class="col-kb" />
              <col class="col-dozent" />
              <col class="col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th
                  [nbTooltip]="'pages.tutors.placements.quick.col.agentTooltip' | translate"
                  nbTooltipPlacement="top"
                >
                  {{ 'pages.tutors.placements.quick.col.agent' | translate }}
                  <nb-icon icon="info-outline" class="col-info"></nb-icon>
                </th>
                <th
                  [nbTooltip]="'pages.tutors.placements.quick.col.kbTooltip' | translate"
                  nbTooltipPlacement="top"
                >
                  {{ 'pages.tutors.placements.quick.col.kb' | translate }}
                  <nb-icon icon="info-outline" class="col-info"></nb-icon>
                </th>
                <th
                  [nbTooltip]="'pages.tutors.placements.quick.col.dozentTooltip' | translate"
                  nbTooltipPlacement="top"
                >
                  {{ 'pages.tutors.placements.quick.col.dozent' | translate }}
                  <nb-icon icon="info-outline" class="col-info"></nb-icon>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                app-tutor-kb-row
                *ngFor="let t of pagedAgentTutors; trackBy: trackTutor"
                [tutor]="t"
                [dataSources]="agentDataSources"
                [dozentBots]="agentDozentBots"
                [placements]="placementsForTutor(t.id)"
                [autoBucketDataSourceIds]="autoBucketDataSourceIds"
              ></tr>
            </tbody>
          </table>
        </section>
      </nb-tab>
    </nb-tabset>
  </nb-card-body>
</nb-card>
